import { ViewContainer } from './components/user-contact-table/containers';
import { Box } from '@mui/material';
import { TableToolbar } from './components/user-contact-table/table-toolbar';
import { Table } from './components/user-contact-table/table';
import { useEffect, useState } from 'react';
import { TablePagination } from './components/user-contact-table/TablePaginator';
import { TopSection } from './components/user-contact-table/TopSection';
import { useAppSelector } from '../../redux/store/configureStore';
import { useParams } from 'react-router-dom';
import { useProspectCollections } from '../../hooks/use-prospect-collections';
import { ProspectDto } from '@zaplify/prospects';
import { useProspects } from '../../hooks/use-prospects';

const DEFAULT_PAGE_SIZE = 10;

const UserContactsView = () => {
    const [searchTerm, setSearchTerm] = useState('');
    const queryParams = new URLSearchParams(window.location.search);
    const searchQuery = queryParams.get('q');
    const { id: prospectCollectionId } = useParams();
    if (searchQuery) {
        queryParams.delete('q');
        window.history.replaceState({}, '', `${window.location.pathname}`);
        setSearchTerm(searchQuery);
    }
    const userOrganizationId = useAppSelector((state) => state.user.zaplifyUser?.userOrganization?.id);
    const [page, setPage] = useState(0);
    const [selected, setSelected] = useState<Record<string, ProspectDto>>({});
    const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
    const [numberOfContacts, setNumberOfContacts] = useState<number>(0);
    const { prospectCollections, removeProspectsFromProspectCollection } = useProspectCollections();
    const { getProspects } = useProspects();
    const prospectIds = prospectCollections.find((pc) => pc.id === prospectCollectionId)?.prospectIds || [];
    const [prospectsInView, setProspectsInView] = useState<ProspectDto[] | null>(null);
    const currentPageNumber = page + 1;

    const updateProspectsInView = async ({
        searchTerm,
        pageSize,
        currentPageNumber,
    }: {
        searchTerm: string;
        pageSize: number;
        currentPageNumber: number;
    }) => {
        const prospects = await getProspects({
            searchTerm,
            userOrganizationId,
            pageSize: pageSize,
            page: currentPageNumber,
            prospectCollectionId,
        });
        setProspectsInView([...prospects.prospects]);
        setNumberOfContacts(prospects.count);
    };

    useEffect(() => {
        setPage(0);
    }, [searchTerm, pageSize]);

    const handleSelect = (prospects: ProspectDto | ProspectDto[]) => {
        if (Array.isArray(prospects)) {
            const newSelected = prospects.reduce((acc, prospect) => {
                acc[prospect.id] = prospect;
                return acc;
            }, {});
            setSelected(newSelected);
        } else {
            setSelected((prevSelected) => {
                const newSelected = { ...prevSelected };
                if (newSelected[prospects.id]) {
                    delete newSelected[prospects.id];
                } else {
                    newSelected[prospects.id] = prospects;
                }
                return newSelected;
            });
        }
    };

    useEffect(() => {
        // if the group of a selected contact is altered, make sure to update the selected contact
        const newSelected = { ...selected };
        for (const prospectId in selected) {
            const contact = prospectsInView.find((p) => p.id === prospectId);
            if (!contact) {
                delete newSelected[prospectId];
            } else {
                newSelected[prospectId] = contact;
            }
        }
        setSelected(newSelected);
    }, [prospectsInView]);

    useEffect(() => {
        updateProspectsInView({ searchTerm, pageSize, currentPageNumber });
    }, [searchTerm, pageSize, currentPageNumber, userOrganizationId, prospectIds]);

    const handleRemoveContacts = async (prospectIds: string[]) => {
        await removeProspectsFromProspectCollection(prospectCollectionId, prospectIds);
        setSelected({});
        updateProspectsInView({ searchTerm, pageSize, currentPageNumber });
    };

    return (
        <ViewContainer>
            <TopSection
                numberOfContacts={prospectIds.length}
                prospectCollectionName={prospectCollections.find((pc) => pc.id === prospectCollectionId)?.name}
            />
            <TableToolbar
                searchTerm={searchTerm}
                setSearchTerm={setSearchTerm}
                prospectIds={prospectIds}
                selected={selected}
                removeUserContacts={handleRemoveContacts}
                prospectCollectionId={prospectCollectionId}
            />
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    maxHeight: '80vh',
                    boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.05)',
                }}
            >
                <Box sx={{ flex: '1 1 auto', overflow: 'auto' }}>
                    <Table
                        prospects={prospectsInView}
                        loading={false}
                        selectedRows={selected}
                        onSelectRow={handleSelect}
                    />
                </Box>
                <Box sx={{ flexShrink: 0, position: 'sticky', bottom: 0, backgroundColor: 'white', zIndex: 1 }}>
                    <TablePagination
                        currentPage={currentPageNumber}
                        rowsPerPageOptions={[10, 25, 50]}
                        count={numberOfContacts}
                        rowsPerPage={pageSize}
                        onPageChange={(number: number) => {
                            setPage(number - 1);
                        }}
                        onRowsPerPageChange={setPageSize}
                        loading={false}
                    />
                </Box>
            </Box>
        </ViewContainer>
    );
};

export default UserContactsView;
